import { useFeaturedEvents } from "@/hooks/useFeaturedEvents";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Calendar, MapPin, Star, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { CompactEventCountdown } from "@/components/CompactEventCountdown";

export const FeaturedEventsCarousel = () => {
  const { data: events, isLoading } = useFeaturedEvents();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-64" />
        ))}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return null;
  }

  return (
    <section className="py-12 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-6 w-6 text-primary fill-primary" />
              <h2 className="text-3xl font-bold">Featured Events</h2>
            </div>
            <p className="text-muted-foreground">
              Don't miss these upcoming Bitcoin events
            </p>
          </div>
          <Link to="/events">
            <Button variant="outline">
              View All Events
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent>
            {events.map((event) => (
              <CarouselItem key={event.id} className="md:basis-1/2 lg:basis-1/3">
                <Card className="h-full border-primary/20 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className="bg-primary/10 text-primary border-primary/20">
                        <Star className="h-3 w-3 mr-1 fill-primary" />
                        Featured
                      </Badge>
                      {event.package_type === "dedicated" && (
                        <Badge variant="outline">Premium</Badge>
                      )}
                    </div>

                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt={event.event_name}
                        className="w-full h-40 object-cover rounded-lg mb-4"
                      />
                    )}

                    <h3 className="text-xl font-bold mb-3 line-clamp-2">
                      {event.event_name}
                    </h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{format(new Date(event.event_date), "MMM dd, yyyy")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CompactEventCountdown eventDate={event.event_date} />
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {event.description}
                    </p>

                    <div className="flex gap-2">
                      {event.has_dedicated_page && event.slug ? (
                        <Button asChild className="flex-1">
                          <Link to={`/events/${event.slug}`}>
                            View Details
                          </Link>
                        </Button>
                      ) : (
                        <Button asChild variant="outline" className="flex-1">
                          <a
                            href={event.event_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Register Now
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
    </section>
  );
};
